import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { WidgetContext } from "src/contexts/WidgetContext";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import LoaderFacts from "../Loader/Loader";
import RostersTeamView from "./RostersTeamView";

function RostersListDisplay({ onClose, rostersData, loadingRoster }) {
  const { selectedTeamInfo } = useContext(WidgetContext);
  const [partnerSelected, setPartnerSelected] = useState<string | null>(null);
  const [teamDisplayName, setTeamDisplayName] = useState<string | null>(null);
  const [userIdTeam, setUserIdTeam] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);

  // get the my team name from the user_team data or the selected team info
  const myTeamName = rostersData?.user_team?.team_name || selectedTeamInfo?.teamName || "";

  // get the teams data from the user_team data or the competitor_teams data in rosters data
  const teams = useMemo(() => {
    const list = rostersData?.competitor_teams || rostersData?.rosters || [];
    // filter out the my team from the list
    const competitors = Array.isArray(list) ? list.filter((team) => (team.team_name ? team.team_name !== myTeamName : true)) : [];

    // if the my team name is not found, return the competitors
    if (!myTeamName) {
      return competitors;
    }

    // get the owner name from the user_team data or the selected team info
    const ownerName = rostersData?.user_team?.owner?.owner_name || rostersData?.user_team?.owner_name || rostersData?.user_team?.owner?.username || null;

    // create the my team entry with the my team name and owner name
    const myTeamEntry = {
      team_id: "MY_TEAM",
      team_name: myTeamName,
      owner_name: ownerName,
    };

    // return the my team entry and the competitors
    return [myTeamEntry, ...competitors];
  }, [rostersData, myTeamName]);

  // handle partner click to display the team view
  const handlePartnerClick = (team) => {
    // if the user select my team, display the my team view (user_teams data)
    if (team.team_id === "MY_TEAM") {
      setPartnerSelected("MY_TEAM");
      setTeamDisplayName(`My Team - ${myTeamName}`);
      setUserIdTeam("MY_TEAM");
      setOwnerId(rostersData?.user_team?.owner?.owner_id || rostersData?.user_team?.owner_id || null);
      return;
    }

    // if the user select a competitor team, display the competitor team view (competitor_teams data)
    setPartnerSelected(team.team_id || team.team_name);
    setTeamDisplayName(team.team_name || "Unknown Team");
    setUserIdTeam(team.team_id || null);
    setOwnerId(team?.owner?.owner_id || team?.owner_id || null);
  };

  const goBack = () => {
    setPartnerSelected(null);
    setTeamDisplayName(null);
    setUserIdTeam(null);
    setOwnerId(null);
  };

  return (
    <Stack
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      sx={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        p={1}
        justifyContent="space-between"
        alignItems="center"
        // borderBottom={`1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`}
        sx={{ width: "100%" }}
      >
        <IconButton aria-label="back" onClick={partnerSelected ? goBack : onClose}>
          <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
        </IconButton>
        <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} textAlign={"center"} className={styles.tradeTeam}>
          {partnerSelected ? teamDisplayName : "Pick A Team"}
        </Typography>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
        </IconButton>
      </Stack>

      <Box
        sx={{ width: "100%", flexGrow: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", scrollBehavior: "smooth", paddingBottom: "max(env(safe-area-inset-bottom), 16px)" }}
        className={styles.noScrollbar}
      >
        {loadingRoster ? (
          <LoaderFacts onClose={onClose} goBack={goBack} />
        ) : !partnerSelected ? (
          <Stack
            direction="column"
            spacing={1}
            sx={{
              width: "100%",
              p: 1,
              boxSizing: "border-box",
              bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            {/* Display the teams data (user or competitor teams) */}
            {teams && teams.length > 0 ? (
              teams.map((team) => {
                const isMyTeam = team.team_id === "MY_TEAM";
                const ownerLabel = isMyTeam ? null : team?.owner?.owner_name || team?.owner_name || team?.owner?.username || null;
                // Split label for card layout (team name on top, owner/subtitle below). Old single-line kept for easy revert:
                // const label = isMyTeam ? `My Team - ${team.team_name}` : `${team.team_name || "Unknown Team"}${ownerLabel ? ` / ${ownerLabel}` : ""}`;
                const teamNameLabel = isMyTeam ? "My Team" : team.team_name || "Unknown Team";
                const subtitleLabel = isMyTeam ? team.team_name : ownerLabel;

                return (
                  <Button
                    key={team.team_id || team.team_name}
                    fullWidth
                    variant="contained"
                    disableElevation
                    data-roster-team={isMyTeam ? "MY_TEAM" : team.team_name}
                    onClick={() => handlePartnerClick(team)}
                    className={styles.tradeTeamName}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      height: "70px",
                      borderRadius: "8px",
                      p: 0,
                      textTransform: "none",
                      // border: none resets all sides — re-apply borderLeft after it
                      border: "none !important",
                      borderLeft: "10px solid #555555 !important",
                      backgroundColor: "#222222",
                      boxShadow: "none !important",
                      "&:hover": {
                        border: "none !important",
                        borderLeft: "10px solid #555555 !important",
                        backgroundColor: "#222222",
                        boxShadow: "none !important",
                      },
                      "&:active, &.Mui-focusVisible": {
                        border: "none !important",
                        borderLeft: "10px solid #555555 !important",
                        boxShadow: "none !important",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "center",

                        width: "100%",
                        px: 1.5,
                        gap: 0.25,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#ffffff",
                          fontWeight: "bold",
                          fontSize: "24px",
                          textAlign: "left",
                          lineHeight: 1.2,
                          fontFamily: "Roboto Condensed",
                        }}
                      >
                        {teamNameLabel}
                      </Typography>
                      {subtitleLabel && (
                        <Typography
                          sx={{
                            color: "#FF672D",
                            fontWeight: "regular",
                            fontSize: "16px",
                            textAlign: "left",
                            lineHeight: 1.2,
                            // opacity: 0.85,
                            fontFamily: "Roboto Condensed",
                          }}
                        >
                          {subtitleLabel}
                        </Typography>
                      )}
                    </Box>
                  </Button>
                );
              })
            ) : (
              <Typography sx={{ color: "white", p: 2 }}>No rosters found.</Typography>
            )}
          </Stack>
        ) : (
          <RostersTeamView onClose={onClose} goBack={goBack} teamDisplayName={teamDisplayName} userIdTeam={userIdTeam} rostersData={rostersData} />
        )}
      </Box>
    </Stack>
  );
}

export default RostersListDisplay;
